/**
 * Gradient rendering utilities for Konva shapes
 */

import { GradientFill, GradientStop } from '../editing/types';

/**
 * Apply gradient fill to a Konva shape node
 * @param node - Konva shape node (Rect, Circle, etc.)
 * @param gradient - Gradient configuration
 * @param width - Shape width
 * @param height - Shape height
 */
export function applyGradientFill(
  node: any,
  gradient: GradientFill,
  width: number,
  height: number
): void {
  if (!node || !gradient || !gradient.stops || gradient.stops.length < 2) {
    return;
  }

  // Sort stops by position
  const sortedStops = [...gradient.stops].sort((a, b) => a.position - b.position);
  
  // Build color stops array for Konva (position, color, position, color, ...)
  const colorStops: (number | string)[] = [];
  sortedStops.forEach((stop) => {
    colorStops.push(stop.position);
    colorStops.push(stop.color);
  });

  switch (gradient.type) {
    case 'linear':
      applyLinearGradient(node, gradient, colorStops, width, height);
      break;
    case 'radial':
      applyRadialGradient(node, gradient, colorStops, width, height);
      break;
    case 'angular':
      applyAngularGradient(node, gradient, colorStops, width, height);
      break;
    case 'diamond':
      applyDiamondGradient(node, gradient, colorStops, width, height);
      break;
  }
}

function applyLinearGradient(
  node: any,
  gradient: GradientFill,
  colorStops: (number | string)[],
  width: number,
  height: number
): void {
  const angle = (gradient.angle || 0) * (Math.PI / 180);
  
  // Calculate start and end points based on angle
  const centerX = width / 2;
  const centerY = height / 2;
  const length = Math.sqrt(width * width + height * height);
  
  const startX = centerX - (length / 2) * Math.cos(angle);
  const startY = centerY - (length / 2) * Math.sin(angle);
  const endX = centerX + (length / 2) * Math.cos(angle);
  const endY = centerY + (length / 2) * Math.sin(angle);
  
  node.fillLinearGradientStartPoint({ x: startX, y: startY });
  node.fillLinearGradientEndPoint({ x: endX, y: endY });
  node.fillLinearGradientColorStops(colorStops);
}

function applyRadialGradient(
  node: any,
  gradient: GradientFill,
  colorStops: (number | string)[],
  width: number,
  height: number
): void {
  const centerX = (gradient.centerX ?? 0.5) * width;
  const centerY = (gradient.centerY ?? 0.5) * height;
  const radius = Math.min(width, height) / 2;
  
  node.fillRadialGradientStartPoint({ x: centerX, y: centerY });
  node.fillRadialGradientEndPoint({ x: centerX, y: centerY });
  node.fillRadialGradientStartRadius(0);
  node.fillRadialGradientEndRadius(radius);
  node.fillRadialGradientColorStops(colorStops);
}

function applyAngularGradient(
  node: any,
  gradient: GradientFill,
  colorStops: (number | string)[],
  width: number,
  height: number
): void {
  // Angular gradients in Konva are approximated using radial gradients with multiple stops
  // We'll use a radial gradient that wraps around
  const centerX = (gradient.centerX ?? 0.5) * width;
  const centerY = (gradient.centerY ?? 0.5) * height;
  const radius = Math.min(width, height) / 2;
  
  // For angular, we need to create a circular pattern
  // This is a simplified version - full angular support would require custom rendering
  node.fillRadialGradientStartPoint({ x: centerX, y: centerY });
  node.fillRadialGradientEndPoint({ x: centerX, y: centerY });
  node.fillRadialGradientStartRadius(0);
  node.fillRadialGradientEndRadius(radius);
  node.fillRadialGradientColorStops(colorStops);
}

function applyDiamondGradient(
  node: any,
  gradient: GradientFill,
  colorStops: (number | string)[],
  width: number,
  height: number
): void {
  // Diamond gradients are approximated using radial gradients
  // Full diamond support would require custom rendering with Shape component
  const centerX = (gradient.centerX ?? 0.5) * width;
  const centerY = (gradient.centerY ?? 0.5) * height;
  const radius = Math.min(width, height) / 2;
  
  node.fillRadialGradientStartPoint({ x: centerX, y: centerY });
  node.fillRadialGradientEndPoint({ x: centerX, y: centerY });
  node.fillRadialGradientStartRadius(0);
  node.fillRadialGradientEndRadius(radius);
  node.fillRadialGradientColorStops(colorStops);
}

/**
 * Create a default gradient (useful for UI)
 */
export function createDefaultGradient(type: 'linear' | 'radial' | 'angular' | 'diamond'): GradientFill {
  return {
    type,
    stops: [
      { color: '#FFFFFF', position: 0 },
      { color: '#000000', position: 1 },
    ],
    angle: type === 'linear' ? 90 : undefined,
    centerX: (type === 'radial' || type === 'angular' || type === 'diamond') ? 0.5 : undefined,
    centerY: (type === 'radial' || type === 'angular' || type === 'diamond') ? 0.5 : undefined,
  };
}

/**
 * Check if a fill value is a gradient
 */
export function isGradientFill(fill: string | GradientFill | undefined): fill is GradientFill {
  return typeof fill === 'object' && fill !== null && 'type' in fill && 'stops' in fill;
}


